import { FC } from 'react';
import { Field, FieldProps, Formik } from 'formik';
// @ts-ignore
import { RadioGroup } from 'react-radio-buttons';
import { AlertComponent, Box, Button, FieldRow, Flatfile, Flex, Modal, RadioButton, StyledFormSelectFormik } from '.';
import { client } from '../../apollo';
import {
  ALL_PEOPLE,
  ALL_PEOPLE_ADMINPORTAL,
  ALL_STAKEHOLDER_TYPES,
  CREATE_STAKEHOLDERS,
  GET_MINISTRIES,
} from '../../graphql';
import { flatfilePeopleValidator, parseErrorMessage, stripSelectValues } from '../../utils';

import {
  COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
  COMMUNITY_MEMBER_STAKEHOLDER_TEMPLATE,
  DISTRICT_OFFICIALS_STAKEHOLDER_TEMPLATE,
  LEARNERS_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
  LEARNERS_STAKEHOLDER_TEMPLATE,
  PROVINCIAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
  SCHOOL_OFFICIALS_STAKEHOLDER_TEMPLATE,
  NATIONAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
  SOCIAL_SERVICES_PROFESSIONAL_STAKEHOLDER_TEMPLATE,
  STUDENT_STAKEHOLDER_TEMPLATE,
} from '../../consts';
import {
  commonPeopleFields,
  communityMemberFields,
  districtOfficialsFields,
  commonAssociationFields,
  commonLearnerAssociationFields,
  learnersFields,
  provincialOfficialsFields,
  schoolOfficialsFields,
  nationalOfficialsFields,
  socialServicesProfessionalsFields,
  studentFields,
  FieldType as FlatFileFieldType,
} from '../../consts/flatfileFields';
import { Option } from '../../utils/types';
import { TemplateType } from '../../consts/uploadTemplates';
import { Label } from '../../typography';
import { formatPeopleForBulkUpload } from '../../utils/format';

interface Props {
  addStakeholderToOrganisation?: boolean;
  setShowUploadModal: (value: boolean) => void;
  showUploadModal: boolean;
  isAdminPortal: boolean;
}

const getFields = (addStakeholderToOrganisation: boolean, type: string): FlatFileFieldType[] => {
  const association = {
    'Community Members': [...commonAssociationFields],
    'District Officials': [...commonAssociationFields],
    Learners: [...commonAssociationFields, ...commonLearnerAssociationFields],
    'Provincial Officials': [...commonAssociationFields],
    'School Officials': [...commonAssociationFields],
    'National Officials': [...commonAssociationFields],
    'Social Services Professionals': [...commonAssociationFields],
    Students: [...commonAssociationFields],
  };
  const creation = {
    'Community Members': [...commonPeopleFields, ...communityMemberFields],
    'District Officials': [...commonPeopleFields, ...districtOfficialsFields],
    Learners: [...commonPeopleFields, ...learnersFields],
    'Provincial Officials': [...commonPeopleFields, ...provincialOfficialsFields],
    'School Officials': [...commonPeopleFields, ...schoolOfficialsFields],
    'National Officials': [...commonPeopleFields, ...nationalOfficialsFields],
    'Social Services Professionals': [...commonPeopleFields, ...socialServicesProfessionalsFields],
    Students: [...commonPeopleFields, ...studentFields],
  };
  if (addStakeholderToOrganisation) {
    let fields: FlatFileFieldType[] | undefined = association[type];
    if (!fields) fields = [];
    return fields;
  } else {
    let fields: FlatFileFieldType[] | undefined = creation[type];
    if (!fields) fields = [];
    return fields;
  }
};

const getTemplate = (addStakeholderToOrganisation: boolean, type: string): TemplateType | undefined => {
  const association = {
    'Community Members': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'District Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    Learners: LEARNERS_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'Provincial Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'School Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'National Officials': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    'Social Services Professionals': COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
    Students: COMMON_ASSOCIATE_PEOPLE_TO_SP_TEMPLATE,
  };
  const creation = {
    'Community Members': COMMUNITY_MEMBER_STAKEHOLDER_TEMPLATE,
    'District Officials': DISTRICT_OFFICIALS_STAKEHOLDER_TEMPLATE,
    Learners: LEARNERS_STAKEHOLDER_TEMPLATE,
    'Provincial Officials': PROVINCIAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
    'School Officials': SCHOOL_OFFICIALS_STAKEHOLDER_TEMPLATE,
    'National Officials': NATIONAL_OFFICIALS_STAKEHOLDER_TEMPLATE,
    'Social Services Professionals': SOCIAL_SERVICES_PROFESSIONAL_STAKEHOLDER_TEMPLATE,
    Students: STUDENT_STAKEHOLDER_TEMPLATE,
  };
  if (addStakeholderToOrganisation) {
    const template: TemplateType | undefined = association[type];
    return template;
  } else {
    const template: TemplateType | undefined = creation[type];
    return template;
  }
};

type FormType = {
  ministryId: Option | undefined;
  typeId: Option | undefined;
  uploadData: any;
  fields: FlatFileFieldType[];
  updateOrNew: string;
};

const INITIAL_VALUES: FormType = {
  ministryId: undefined,
  typeId: undefined,
  uploadData: undefined,
  fields: [
    // ...commonAssociationFields,
    // ...commonLearnerAssociationFields,
    // ...commonPeopleFields,
    // ...communityMemberFields,
    // ...districtOfficialsFields,
    ...learnersFields,
    // ...provincialOfficialsFields,
    // ...schoolOfficialsFields,
    // ...nationalOfficialsFields,
    // ...socialServicesProfessionalsFields,
    // ...studentFields,
  ],
  updateOrNew: 'new',
};

const UploadPeopleModal: FC<Props> = props => {
  const addStakeholderToOrganisation =
    props.addStakeholderToOrganisation === undefined ? false : props.addStakeholderToOrganisation;
  return (
    <Modal
      open={props.showUploadModal}
      onClose={() => props.setShowUploadModal(!props.showUploadModal)}
      heading="Upload Data"
      bodyOverflowVisible={true}
      width={'1000px'}
    >
      <Formik<FormType>
        initialValues={INITIAL_VALUES}
        onSubmit={async values => {
          const copyOfValues = Object.assign({}, values);
          const formattedValues: any = stripSelectValues(copyOfValues);
          const uploadedData = formattedValues.uploadData.map((b: any) => formatPeopleForBulkUpload(b));

          return await client
            .mutate({
              mutation: CREATE_STAKEHOLDERS,
              variables: {
                inputs: {
                  ministryId: formattedValues.ministryId,
                  typeId: formattedValues.typeId,
                  values: uploadedData,
                  updateOrNew: formattedValues.updateOrNew,
                },
              },
              refetchQueries: [
                {
                  query: props.isAdminPortal ? ALL_PEOPLE_ADMINPORTAL : ALL_PEOPLE,
                  variables: { input: { first: 10, status: 'ACTIVE' } },
                },
              ],
            })
            .then(() => 'Success!')
            .catch(e => {
              const msg = parseErrorMessage((e && e.message) || e);
              console.error(msg);
              return msg;
            });
        }}
        render={formikProps => (
          <Flex direction="column" size="1" px={3}>
            <Flex direction="column" size="1" flexWrap="nowrap" justify="space-around" minHeight={170}>
              <FieldRow>
                <Field
                  name="ministryId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Ministry"
                      form={formikProps}
                      isClearable={true}
                      mr={0}
                      query={{
                        query: GET_MINISTRIES,
                      }}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="typeId"
                  render={({ field }: FieldProps<FormType>) => (
                    <StyledFormSelectFormik
                      {...field}
                      field={field}
                      label="Stakeholder Type"
                      form={formikProps}
                      isClearable={true}
                      changeHandler={(item: any) => {
                        if (item) {
                          const fields = getFields(addStakeholderToOrganisation, item.label);
                          formikProps.setFieldValue('fields', fields);
                        } else {
                          formikProps.setFieldValue('fields', []);
                        }
                      }}
                      mr={0}
                      query={{
                        query: ALL_STAKEHOLDER_TYPES,
                        variables: {
                          input: {
                            ...(formikProps.values.ministryId && { ministryId: formikProps.values.ministryId.value }),
                          },
                        },
                      }}
                    />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <FieldRow flex={0.5}>
                  <Field
                    name="updateOrNew"
                    render={({ field }: FieldProps<FormType>) => (
                      <Box {...formikProps} flexDirection={'column'}>
                        <Label htmlFor="updateOrNew">Select option:</Label>
                        <RadioGroup
                          value={formikProps.values.updateOrNew}
                          onChange={(value: string) => {
                            formikProps.setFieldValue('updateOrNew', value);
                          }}
                          horizontal={true}
                        >
                          <RadioButton value="new">New</RadioButton>
                          <RadioButton value="update">Update</RadioButton>
                        </RadioGroup>
                      </Box>
                    )}
                  />
                </FieldRow>
              </FieldRow>
              <FieldRow>
                <AlertComponent
                  title={`Instructions`}
                  message={`
                    Please upload a maximum of 1500 records at a time

                    Note that CSV values are sensitive to:
                      - Typographical errors
                      - Text Case
                      - Naming conventions
                  `}
                />
              </FieldRow>
            </Flex>
            <FieldRow>
              <Flex direction="row" size="1" flexWrap="nowrap" justify="space-between">
                <Field
                  name="uploadData"
                  render={({ field }: FieldProps<FormType>) => {
                    const template = !!formikProps.values.typeId
                      ? getTemplate(addStakeholderToOrganisation, formikProps.values.typeId.label)
                      : undefined;
                    return (
                      <Flatfile
                        csvFileData={template}
                        csvFileName={`${formikProps.values.typeId &&
                          formikProps.values.typeId.label}StakeholdersTemplate.csv`}
                        flatFileDataType="People"
                        flatFileFields={formikProps.values.fields}
                        flatFileSubmitHandler={async (results: any) => {
                          // check total number of entries
                          if (results.length > 1500) {
                            throw new Error(
                              'Client error - Please only upload a maximum of 1500 stakeholders at a time'
                            );
                          }

                          formikProps.values.uploadData = results;

                          return formikProps.handleSubmit();
                        }}
                        flatFileValidators={flatfilePeopleValidator}
                        close={() => props.setShowUploadModal(!props.showUploadModal)}
                      />
                    );
                  }}
                />
                <Button type="button" text="Template" bg="warning" width="40px" />
              </Flex>
            </FieldRow>
          </Flex>
        )}
      />
    </Modal>
  );
};

export default UploadPeopleModal;
